/* eslint-disable import/prefer-default-export */
import * as d3 from 'd3'
import { CompToolPlayer, PlayerRow } from './types.ts'
import { groupStatSelects } from './stat-groupings.ts'
import type { Dimensions } from './responsive-dimensions.tsx'

import {
    SVG_HEIGHT,
    // TOP_BUFFER,
    STAT_HEADER_GAP,
    LINE_HEIGHT,
    // ADD_BUTTON_DIM,
    TITLE_HEIGHT,
    POSITION_MAP,
    TEAM_LOGO_SIZE,
} from './constants.ts'

export const colorScale = d3
    .scaleLinear<string>()
    .domain([0, 50, 100])
    .range(['#3661AD', '#B4CFD1', '#D82129'])

export function updateUrl(
    selectedPlayers: string[],
    selectedStats: string[],
    showWinner: boolean,
    showValues: boolean,
    showTeams: boolean,
    showTitle: boolean,
    title: string
) {
    const params = new URLSearchParams()
    selectedPlayers.forEach((player) => params.append('player', player))
    selectedStats.forEach((stat) => params.append('stat', stat))

    if (showWinner) {
        params.append('showWinner', 'true')
    }

    if (showValues) {
        params.append('showValues', 'true')
    }

    if (!showTeams) {
        params.append('hideTeams', 'true')
    }

    if (showTitle && title.length > 0) {
        params.append('showTitle', 'true')
        params.append('title', title)
    }

    window.history.pushState({}, '', `?${params.toString()}`)
}

export function getUniqueId(player: CompToolPlayer) {
    return `${player.id}-${player.year}-${player.grouping_cat.toLowerCase()}`
}

function estimatePercentile(z: number) {
    // zScore == number of standard deviations from the mean

    // if z is greater than 6.5 standard deviations from the mean
    // the number of significant digits will be outside of a reasonable
    // range
    const min = 1
    if (z < -6.5) return min
    if (z > 6.5) return 100

    let factK = 1
    let sum = 0
    let term = 1
    let k = 0
    const loopStop = Math.exp(-23)
    while (Math.abs(term) > loopStop) {
        term =
            (((0.3989422804 * (-1) ** k * z ** k) / (2 * k + 1) / 2 ** k) *
                z ** (k + 1)) /
            factK
        sum += term
        k += 1
        factK *= k
    }
    sum += 0.5

    return Math.max(min, Math.round(sum * 100))
}

export function getFakePercentile(
    value: number,
    avg: number,
    stdev: number,
    inverse: boolean
) {
    if (!avg || !stdev) {
        return 50
    }

    const diff = inverse ? avg - value : value - avg
    const zScore = diff / stdev

    return estimatePercentile(zScore)
}

export function getType(selectedData: {
    players: {
        id: string
        displayOrder: number
    }[]
    dataToUse: PlayerRow[]
}): 'batter' | 'pitcher' {
    if (!selectedData.players.length) {
        return 'batter'
    }

    const possibleType = selectedData.players[0].id.split('-')[2]

    return possibleType === 'pitcher' ? 'pitcher' : 'batter'
}

export function isStatsFull(
    selectedStats: (keyof PlayerRow)[],
    currentType: 'batter' | 'pitcher',
    showTitle: boolean,
    showTeams: boolean,
    ignoreAddButton: boolean,
    dimensions: Dimensions
) {
    const { ADD_BUTTON_DIM, TOP_BUFFER } = dimensions
    const groupedStats = groupStatSelects(selectedStats, currentType)
    const numGroups = Object.entries(groupedStats).filter(
        ([, stats]) => stats.length > 0
    ).length

    const numStats = selectedStats.length
    const titleOffset = showTitle ? TITLE_HEIGHT : 0
    const addButtonOffset = ignoreAddButton ? 10 : ADD_BUTTON_DIM
    const teamOffset = showTeams ? 0 : -TEAM_LOGO_SIZE

    const currHeight =
        TOP_BUFFER +
        STAT_HEADER_GAP * numGroups +
        LINE_HEIGHT * numStats +
        addButtonOffset +
        titleOffset +
        teamOffset

    return currHeight >= SVG_HEIGHT
}

export function getPosString(
    id: string | number,
    pos: string,
    type: 'Batter' | 'Pitcher'
) {
    if (Number(id) === 660271) {
        if (type === 'Batter') {
            return 'DH'
        }
        return 'P'
    }

    return POSITION_MAP[Number(pos)]
}
